import { useCallback, useEffect, useState } from 'react';
import { workflowAPI, WorkflowAccess } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Whether the signed-in user may open the embedded n8n editor (admin-granted
 * per user). Cached per user like useTaskAccess; call
 * {@link invalidateWorkflowAccess} after a change that affects it. A failed
 * load is not cached, so a retry (or the next mount) asks the server again.
 */
let cache: { userId: number; promise: Promise<WorkflowAccess> } | null = null;
/** Mounted hooks, told to reload when one of them retries (so the sidebar link comes back too). */
const listeners = new Set<() => void>();

export const invalidateWorkflowAccess = () => {
  cache = null;
};

export interface WorkflowAccessState {
  /** Null while loading, or after a failed load. */
  access: WorkflowAccess | null;
  /** The last load failed (network, server restart, 5xx). */
  failed: boolean;
  /** Asks the server again. */
  retry: () => void;
}

export const useWorkflowAccessState = (): WorkflowAccessState => {
  const { user } = useAuth();
  const [access, setAccess] = useState<WorkflowAccess | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped to re-run the load.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const reload = () => setAttempt((n) => n + 1);
    listeners.add(reload);
    return () => { listeners.delete(reload); };
  }, []);

  useEffect(() => {
    if (!user) {
      setAccess(null);
      setFailed(false);
      return;
    }
    if (!cache || cache.userId !== user.userId) {
      const promise = workflowAPI.getAccess().then(r => r.data);
      const entry = { userId: user.userId, promise };
      cache = entry;
      // Drop a failed load so the next attempt asks again.
      promise.catch(() => { if (cache === entry) cache = null; });
    }
    let alive = true;
    cache.promise.then(
      (a) => { if (alive) { setAccess(a); setFailed(false); } },
      () => { if (alive) { setAccess(null); setFailed(true); } },
    );
    return () => { alive = false; };
  }, [user, attempt]);

  const retry = useCallback(() => {
    cache = null;
    setFailed(false);
    listeners.forEach((reload) => reload());
  }, []);

  return { access, failed, retry };
};

export const useWorkflowAccess = (): WorkflowAccess | null => useWorkflowAccessState().access;
